import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';

import { type WizardContextType, type WizardDialogProps } from './types';
import WizardContext, { useWizardContext } from './WizardContext';

const WizardDialog = <WizardData = Record<string, unknown>,>({
  open,
  isLoading = false,
  onCancel,
  onComplete,
  steps,
}: WizardDialogProps<WizardData>) => {
  const [wizardData, setWizardData] = useState<WizardData>({} as WizardData);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentOnComplete, setCurrentOnComplete] = useState<(() => Promise<boolean>) | null>(null);
  const [isAdvancing, setIsAdvancing] = useState(false);

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const updateWizardData = useCallback((data: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...data }));
  }, []);

  const registerOnComplete = useCallback((handler: () => Promise<boolean>) => {
    setCurrentOnComplete(() => handler);
  }, []);

  const resetWizard = useCallback(() => {
    setCurrentStepIndex(0);
    setWizardData({} as WizardData);
    setIsAdvancing(false);
  }, []);

  const handleAfterClose = useCallback(() => {
    resetWizard();
  }, [resetWizard]);

  const handleDialogOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onCancel?.();
      }
    },
    [onCancel],
  );

  const handleDialogOpenChangeComplete = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        handleAfterClose();
      }
    },
    [handleAfterClose],
  );

  const advanceStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      const nextIndex = prev + 1;
      const maxIndex = steps.length - 1;
      return Math.min(nextIndex, maxIndex);
    });
  }, [steps.length]);

  const completeWizard = useCallback(() => {
    onComplete?.(wizardData);
  }, [wizardData, onComplete]);

  const goToNextStep = useCallback(() => {
    if (isAdvancing) {
      return;
    }

    setIsAdvancing(true);

    if (!isLastStep) {
      advanceStep();
      setIsAdvancing(false);
    } else {
      completeWizard();
    }
  }, [isLastStep, isAdvancing, advanceStep, completeWizard]);

  const handleCustomValidation = useCallback(async () => {
    if (!currentOnComplete) return false;

    try {
      const canProceed = await currentOnComplete();
      return canProceed;
    } catch {
      return false;
    }
  }, [currentOnComplete]);

  const handleNext = useCallback(async () => {
    const hasNoValidation = !currentOnComplete;
    const customValidationPassed = await handleCustomValidation();
    const validationPassed = customValidationPassed || hasNoValidation;

    if (!validationPassed) return;

    if (!isLastStep) {
      advanceStep();
    } else {
      completeWizard();
    }
  }, [currentOnComplete, isLastStep, advanceStep, completeWizard, handleCustomValidation]);

  const contextValue: WizardContextType<WizardData> = useMemo(
    () => ({
      wizardData,
      updateWizardData,
      registerOnComplete,
      goToNextStep,
      currentStepIndex,
      isFirstStep,
      isLastStep,
    }),
    [
      wizardData,
      updateWizardData,
      registerOnComplete,
      goToNextStep,
      currentStepIndex,
      isFirstStep,
      isLastStep,
    ],
  );

  const currentStep = steps[currentStepIndex];
  const title = currentStep
    ? typeof currentStep.title === 'function'
      ? currentStep.title(wizardData)
      : currentStep.title || ''
    : '';

  const buttonText = currentStep?.nextButtonText ? (
    typeof currentStep.nextButtonText === 'function' ? (
      currentStep.nextButtonText(wizardData)
    ) : (
      currentStep.nextButtonText
    )
  ) : isLastStep ? (
    <Trans>Finish</Trans>
  ) : (
    <Trans>Next</Trans>
  );

  const nextButtonVariant = currentStep?.nextButtonDanger ? 'primary' : 'on-primary';
  const nextButtonDanger = !!currentStep?.nextButtonDanger;

  return (
    <WizardContext.Provider value={contextValue as WizardContextType}>
      <Dialog
        open={open}
        onOpenChange={handleDialogOpenChange}
        onOpenChangeComplete={handleDialogOpenChangeComplete}
      >
        <DialogContent showCloseButton={!isLoading}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div>{currentStep?.component}</div>
          <DialogFooter>
            <DialogClose
              render={
                <Button size="large" disabled={isLoading}>
                  <Trans>Cancel</Trans>
                </Button>
              }
            />
            <Button
              size="large"
              variant={nextButtonVariant}
              danger={nextButtonDanger}
              isLoading={isLoading}
              onClick={handleNext}
            >
              {buttonText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WizardContext.Provider>
  );
};

WizardDialog.useWizardInstance = useWizardContext;

export default WizardDialog;
